import {
  ActionFunctionArgs,
  createContext,
  LoaderFunctionArgs,
  MiddlewareFunction,
  redirect,
  useLoaderData,
  useRouteLoaderData,
} from "react-router";
import {
  AccountsWithNameByApplication,
  CertificateRepresentation,
  OAuthClientCreationRequest,
  OAuthClientRepresentation,
  UpdateClientRequest,
} from "../infrastructure/api/api";
import { MissingLinkError } from "../infrastructure/entrypoint/link";
import {
  fetchAvailableAccounts,
  organizationContext,
} from "../infrastructure/organization/OrganizationContext";
import { rootPath } from "../infrastructure/translation/useRootPath";
import { certificatesApi } from "../Certificate/certificatesApi";
import { statusOf } from "../Certificate/certificateStatus";
import { ClientsApi, clientsApi } from "./clientsApi";
import { clientPaths } from "./useClientPaths";

export const CLIENTS_ROUTE_ID = "clients";

const clientsContext = createContext<ClientsApi>();

export const clientsMiddleware: MiddlewareFunction = async (
  { context },
  next,
) => {
  context.set(clientsContext, clientsApi(context.get(organizationContext)));

  await next();
};

interface ClientsRouteData {
  clients: OAuthClientRepresentation[];
  availableAccounts: AccountsWithNameByApplication;
  certificates: CertificateRepresentation[];
}

export const clientsLoader = async ({
  context,
}: LoaderFunctionArgs): Promise<ClientsRouteData> => {
  const organization = context.get(organizationContext);
  const [clients, availableAccounts, certificates] = await Promise.all([
    context.get(clientsContext).listClients(),
    fetchAvailableAccounts(organization),
    certificatesApi(organization).listCertificates(),
  ]);

  return { clients, availableAccounts, certificates };
};

function useClientsRouteData(): ClientsRouteData {
  const data = useRouteLoaderData<ClientsRouteData>(CLIENTS_ROUTE_ID);
  if (!data) {
    throw new Error(
      `Clients can only be used within the "${CLIENTS_ROUTE_ID}" route`,
    );
  }

  return data;
}

export const useClients = (): OAuthClientRepresentation[] =>
  useClientsRouteData().clients;

export function useClient(
  clientId: string | undefined,
): OAuthClientRepresentation | undefined {
  return useClientsRouteData().clients.find(
    (c) => c.client.clientId === clientId,
  );
}

export const useAvailableAccounts = (): AccountsWithNameByApplication =>
  useClientsRouteData().availableAccounts;

export const useOrganizationCertificates = (): CertificateRepresentation[] =>
  useClientsRouteData().certificates;

export const lacksActiveCertificate = (
  client: OAuthClientRepresentation,
  certificates: CertificateRepresentation[],
): boolean =>
  client.client.certificateIds.length > 0 &&
  !certificates.some(
    (c) =>
      client.client.certificateIds.includes(c.certificate.id) &&
      statusOf(c).isActive,
  );

interface ClientCertificateOptions {
  certificates: CertificateRepresentation[];
}

export const isSelectableCertificate = (c: CertificateRepresentation) =>
  statusOf(c).isActive;

const selectableCertificates = async ({ context }: LoaderFunctionArgs) => {
  const certificates = await certificatesApi(
    context.get(organizationContext),
  ).listCertificates();

  return { certificates: certificates.filter(isSelectableCertificate) };
};

export const newClientLoader = async (
  args: LoaderFunctionArgs,
): Promise<ClientCertificateOptions> => selectableCertificates(args);

export const editClientLoader = async (
  args: LoaderFunctionArgs,
): Promise<ClientCertificateOptions> => selectableCertificates(args);

export const useClientCertificateOptions = (): ClientCertificateOptions =>
  useLoaderData<ClientCertificateOptions>();

interface ClientRouteData {
  certificates: CertificateRepresentation[];
}

export const clientLoader = async ({
  params,
  context,
}: LoaderFunctionArgs): Promise<ClientRouteData> => {
  const clients = await context.get(clientsContext).listClients();
  const client = clients.find((c) => c.client.clientId === params.clientId);

  if (!client) {
    return { certificates: [] };
  }

  const api = certificatesApi(context.get(organizationContext));
  const certificates = await Promise.all(
    client.client.certificateIds.map((id) => api.findCertificate(id)),
  );

  return {
    certificates: certificates.filter(
      (c): c is CertificateRepresentation => c !== undefined,
    ),
  };
};

export const useClientCertificates = (): CertificateRepresentation[] =>
  useLoaderData<ClientRouteData>().certificates;

export interface ClientActionFailure {
  missingLink: boolean;
  message: string;
}

export function isClientActionFailure(
  data: unknown,
): data is ClientActionFailure {
  return typeof data === "object" && data !== null && "missingLink" in data;
}

const failure = (operation: string, err: unknown): ClientActionFailure => {
  console.error(`Failed to ${operation} client:`, err);

  return {
    missingLink: err instanceof MissingLinkError,
    message: err instanceof Error ? err.message : "Unknown error",
  };
};

const paths = ({ params }: ActionFunctionArgs) =>
  clientPaths(rootPath(params.language), params.orgnr!);

export const createClientAction = async (args: ActionFunctionArgs) => {
  const request: OAuthClientCreationRequest = await args.request.json();

  try {
    const created = await args.context
      .get(clientsContext)
      .createClient(request);
    return redirect(paths(args).client(created.client.clientId));
  } catch (err) {
    return failure("create", err);
  }
};

export const updateClientAction = async (args: ActionFunctionArgs) => {
  const clientId = args.params.clientId!;
  const { displayName, certificateIds, scopes }: UpdateClientRequest =
    await args.request.json();

  try {
    await args.context
      .get(clientsContext)
      .updateClient(clientId, { displayName, certificateIds, scopes });
    return redirect(paths(args).client(clientId));
  } catch (err) {
    return failure("update", err);
  }
};

export const deleteClientAction = async (args: ActionFunctionArgs) => {
  try {
    await args.context.get(clientsContext).deleteClient(args.params.clientId!);
    return redirect(paths(args).organization);
  } catch (err) {
    return failure("delete", err);
  }
};
